/*
 * Optimizely Feature Experimentation — shared shopper attribute helpers.
 *
 * Pure and SDK-free so both the /optimizely-page SSR targeting session and the
 * client-side conversion tracking build identical user contexts; decisions and
 * events must share userId + attributes or Optimizely splits the visitor.
 */

/**
 * Normalize customer group IDs (CSV string or array) into a trimmed,
 * de-duplicated array. Group IDs are case-sensitive, so casing is preserved.
 * @param {string|string[]|*} groups
 * @returns {string[]}
 */
function normalizeCustomerGroups(groups) {
    const list = Array.isArray(groups)
        ? groups
        : typeof groups === 'string'
        ? groups.split(',')
        : []
    const ids = list.map((id) => String(id).trim()).filter(Boolean)
    return [...new Set(ids)]
}

/**
 * Build the scalar attribute map sent to Optimizely's user context. Optimizely
 * audiences can only read scalars (string/number/boolean), so customer group
 * membership is exposed two ways for experiment audience scoping: a
 * comma-joined `customerGroups` string (for substring conditions) AND a
 * per-group boolean `group_<ID>: true` (for exact "is true" conditions).
 * Experiment-level group scoping lives in Optimizely's audience config, not CMS.
 * @param {{brandSlug: string, localeId: string, isLoggedIn: boolean, customerGroups: string[]}} params
 * @returns {Object} scalar-only attribute map
 */
function buildOptimizelyAttributes({brandSlug, localeId, isLoggedIn, customerGroups}) {
    const attributes = {
        brand: brandSlug,
        locale: localeId,
        isRegistered: isLoggedIn,
        userType: isLoggedIn ? 'registered' : 'guest',
        customerGroups: customerGroups.join(',')
    }
    customerGroups.forEach((groupId) => {
        attributes[`group_${groupId}`] = true
    })
    return attributes
}

/**
 * Optimizely attributes and event tags accept only scalar values; arrays and
 * objects are dropped so they never reach the SDK.
 * @param {Object} [values]
 * @returns {Object} entries containing only string/number/boolean/null values
 */
function pruneScalars(values) {
    if (!values || typeof values !== 'object') return {}
    return Object.entries(values).reduce((acc, [key, value]) => {
        if (
            value === null ||
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
        ) {
            acc[key] = value
        }
        return acc
    }, {})
}

export {normalizeCustomerGroups, buildOptimizelyAttributes, pruneScalars}
